import React from "react";
import { connect } from "react-redux";
import { bool, string, func, object } from "prop-types";
import { Dropdown } from "react-bootstrap";
import FontAwesome from "react-fontawesome";
import {
  copyEventUrlToClipboard,
  getOdaInviteLink,
} from "utils/helperFunctions";
import { getTestEvents } from "containers/Results/FunctionLib";
import { getPendingEventsActionPermissions } from "./helpers";

const PendingEventsButtons = ({
  candidate,
  candidateWillNotBeReminded,
  currentCandidateId,
  deleteInvitedCandidate,
  eventId,
  fromInvited,
  fromResultsV2,
  jobData,
  moveEmailCandidatesPermission,
  onPendingEventsActionCompletion,
  onShowExtendExpirationModal,
  showVideoInterviewAdjustmentsModal,
  showReleaseScoresModal,
  showReminderErrorModal,
  showReminderModal,
  // testEventId is the unique identifier of the test, while `eventId` is the ID used to access the assessment via oda.
  testEventId,
  tests,
  updateExtendExpirationCandidateIds,
  updateExtendExpirationTestEventIds,
  userData,
  isLiveInterviewEvent,
}) => {
  const isInsightUser = userData && userData.partnerAccountId === 47;

  // on selection handler
  const onDropdownSelection = (event) => {
    switch (event) {
      case "remind":
        if (candidateWillNotBeReminded) {
          showReminderErrorModal(testEventId);
        } else {
          showReminderModal(testEventId, eventId);
        }
        break;
      case "extendExpiration":
        updateExtendExpirationCandidateIds([currentCandidateId]);
        updateExtendExpirationTestEventIds([testEventId]);
        onShowExtendExpirationModal();
        break;
      case "copy": {
        const landingPage = jobData ? jobData.get("landingPage") : null;
        const odaLink = getOdaInviteLink(
          eventId,
          landingPage ? landingPage.get("version") : null
        );
        copyEventUrlToClipboard(odaLink);
        onPendingEventsActionCompletion(
          "The link was successfully copied!",
          false
        );
        break;
      }
      case "delete":
        deleteInvitedCandidate(testEventId, isLiveInterviewEvent);
        break;
      case "release":
        showReleaseScoresModal(testEventId);
        break;
      case "adjustVideoInterview":
        showVideoInterviewAdjustmentsModal(testEventId);
        break;
      default:
        break;
    }
    return true;
  };

  const testEvents = getTestEvents(candidate, fromResultsV2);

  const testEvent =
    testEvents &&
    (testEvents.get(testEventId) ||
      testEvents.get("videoInterview") || // VI-only event in Results v2 data structure uses the key 'videoInterview'
      (testEvents.get("liveInterviews") && // LI in Results v2 data structure uses the key 'liveInterviews'
        testEvents.get("liveInterviews").get(testEventId)));

  const {
    showRemind,
    showExtendExpiration,
    showCopy,
    showDelete,
    showRelease,
    showAdjustVideoInterview,
  } = getPendingEventsActionPermissions(
    fromInvited,
    moveEmailCandidatesPermission,
    tests,
    candidate,
    testEvent,
    isInsightUser,
    isLiveInterviewEvent
  );

  return (
    <Dropdown
      id="actions"
      className="mr-1"
      onSelect={onDropdownSelection}
      alignRight
    >
      <div className="no-caret">
        <Dropdown.Toggle size="sm" variant="outline-secondary">
          <FontAwesome name="ellipsis-h" fixedWidth />
        </Dropdown.Toggle>
      </div>
      <Dropdown.Menu>
        {showRemind ? (
          <Dropdown.Item
            eventKey="remind"
            key={`remind_${testEventId}`}
            className="actionDropdownItem"
          >
            Remind Candidate
          </Dropdown.Item>
        ) : null}
        {showExtendExpiration ? (
          <Dropdown.Item
            eventKey="extendExpiration"
            key={`extendExpiration_${testEventId}`}
            className="actionDropdownItem"
          >
            Extend Expiration
          </Dropdown.Item>
        ) : null}
        {showCopy ? (
          <Dropdown.Item
            eventKey="copy"
            key={`copy_${testEventId}`}
            className="actionDropdownItem"
          >
            Copy Candidate Link
          </Dropdown.Item>
        ) : null}
        {showDelete ? (
          <Dropdown.Item
            eventKey="delete"
            key={`delete_${testEventId}`}
            className="actionDropdownItem"
          >
            {isLiveInterviewEvent
              ? "Delete Live Interview Evaluation"
              : "Delete Candidate Invitation"}
          </Dropdown.Item>
        ) : null}
        {showRelease ? (
          <Dropdown.Item
            eventKey="release"
            key={`release_${testEventId}`}
            className="actionDropdownItem"
          >
            Release Scores
          </Dropdown.Item>
        ) : null}
        {showAdjustVideoInterview ? (
          <Dropdown.Item
            className="actionDropdownItem"
            eventKey="adjustVideoInterview"
            key={`adjustVideoInterview_${testEventId}`}
          >
            Adjusted Time
          </Dropdown.Item>
        ) : null}
      </Dropdown.Menu>
    </Dropdown>
  );
};

PendingEventsButtons.propTypes = {
  candidate: object,
  candidateWillNotBeReminded: bool,
  currentCandidateId: string,
  deleteInvitedCandidate: func.isRequired,
  eventId: string,
  fromInvited: bool,
  fromResultsV2: bool,
  isLiveInterviewEvent: bool,
  jobData: object,
  moveEmailCandidatesPermission: bool,
  onPendingEventsActionCompletion: func.isRequired,
  onShowExtendExpirationModal: func.isRequired,
  showVideoInterviewAdjustmentsModal: func,
  showReleaseScoresModal: func,
  showReminderErrorModal: func.isRequired,
  showReminderModal: func.isRequired,
  testEventId: string,
  tests: object,
  updateExtendExpirationCandidateIds: func.isRequired,
  updateExtendExpirationTestEventIds: func.isRequired,
  userData: object,
};

const mapStateToProps = (state) => ({
  userData: state.get("App").get("userData"),
});

export default connect(mapStateToProps)(PendingEventsButtons);
